import { useEffect, useState } from "react";
import { X, Plus, Trash2, MessageSquare, HelpCircle, StickyNote } from "lucide-react";
import { auth, db } from "../firebase";
import { collection, addDoc, updateDoc, doc, serverTimestamp } from "firebase/firestore";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { DatePicker } from "./ui/date-picker";

// TYPES: Match the types from CoffeeChats.tsx
type ChatStatus = "pending" | "sent" | "done";

type CoffeeChat = {
  id: string;
  name: string;
  company: string;
  role: string;
  email?: string;
  linkedin?: string;
  status: ChatStatus;
  message: string;
  questions: string[];
  notes: string;
  scheduledDate?: string;
  createdAt: any;
};

type AddCoffeeChatProps = {
  isOpen: boolean;
  onClose: () => void;
  editChat?: CoffeeChat;
};

export default function AddCoffeeChat({ isOpen, onClose, editChat }: AddCoffeeChatProps) {
  // Contact Information
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("");
  const [email, setEmail] = useState("");
  const [linkedin, setLinkedin] = useState("");
  
  // Message & Questions
  const [message, setMessage] = useState("");
  const [questions, setQuestions] = useState<string[]>([]);
  const [newQuestion, setNewQuestion] = useState("");
  
  // Notes & Status
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState<ChatStatus>("pending");
  const [scheduledDate, setScheduledDate] = useState("");
  
  // UI State
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<"contact" | "message" | "questions" | "notes">("contact");

  // EFFECT: Populate form when editing an existing chat
  useEffect(() => {
    if (editChat) {
      setName(editChat.name);
      setCompany(editChat.company);
      setRole(editChat.role);
      setEmail(editChat.email || "");
      setLinkedin(editChat.linkedin || "");
      setMessage(editChat.message);
      setQuestions(editChat.questions);
      setNotes(editChat.notes);
      setStatus(editChat.status);
      setScheduledDate(editChat.scheduledDate || "");
    } else {
      resetForm();
    }
  }, [editChat, isOpen]);

  const resetForm = () => {
    setName("");
    setCompany("");
    setRole("");
    setEmail("");
    setLinkedin("");
    setMessage("");
    setQuestions([]);
    setNewQuestion("");
    setNotes("");
    setStatus("pending");
    setScheduledDate("");
    setActiveTab("contact");
  };

  // FUNCTION: Add a new question to the list
  const handleAddQuestion = () => {
    if (!newQuestion.trim()) return;
    setQuestions([...questions, newQuestion.trim()]);
    setNewQuestion("");
  };

  // FUNCTION: Remove a question from the list
  const handleRemoveQuestion = (index: number) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {

    if (!name.trim() || !company.trim() || !role.trim()) {
      alert("Please fill in name, company, and role");
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      alert("Please log in to save coffee chats");
      return;
    }

    setSaving(true);

    try {
      // PREPARE DATA: Create clean data object for Firestore
      const chatData: any = {
        name: name.trim(),
        company: company.trim(),
        role: role.trim(),
        message: message.trim(),
        questions: questions,
        notes: notes.trim(),
        status: status,
      };

      // Only add optional fields if they have values
      if (email.trim()) chatData.email = email.trim();
      if (linkedin.trim()) chatData.linkedin = linkedin.trim();
      if (scheduledDate) chatData.scheduledDate = scheduledDate;

      if (editChat) {
        // UPDATE: Edit existing chat
        await updateDoc(doc(db, "users", user.uid, "coffeeChats", editChat.id), chatData);
        console.log("✅ Coffee chat updated successfully");
      } else {
        // ADD: Create new chat
        await addDoc(collection(db, "users", user.uid, "coffeeChats"), {
          ...chatData,
          createdAt: serverTimestamp(),
        });
        console.log("✅ Coffee chat added successfully");
      }

      resetForm();
      onClose();
    } catch (error) {
      console.error("Error saving coffee chat:", error);
      alert("Failed to save coffee chat. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent showClose={false} className="flex flex-col gap-0 max-w-4xl p-0 overflow-hidden shadow-2xl">
        {/* MODAL HEADER */}
        <div className="border-b border-cocoa/20 px-6 py-4 flex items-center justify-between">
          <div>
            <DialogTitle className="text-xl">
              {editChat ? 'Edit Coffee Chat' : 'Schedule Coffee Chat'}
            </DialogTitle>
            <DialogDescription className="mt-1">
              Prepare for your networking conversation
            </DialogDescription>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md text-espresso hover:bg-cocoa/5 "
          >
            <X size={20} />
          </button>
        </div>

        {/* TAB NAVIGATION */}
        <div className="border-b border-cocoa/20 px-6">
          <div className="flex gap-6">
            <button
              onClick={() => setActiveTab("contact")}
              className={`py-3 px-1 border-b-2 text-sm font-medium transition ${
                activeTab === "contact"
                  ? "border-caramel text-caramel"
                  : "border-transparent text-cocoa hover:text-espresso"
              }`}
            >
              Contact Info
            </button>
            <button
              onClick={() => setActiveTab("message")}
              className={`py-3 px-1 border-b-2 text-sm font-medium transition flex items-center gap-2 ${
                activeTab === "message"
                  ? "border-caramel text-caramel"
                  : "border-transparent text-cocoa hover:text-espresso"
              }`}
            >
              <MessageSquare size={14} />
              Message
            </button>
            <button
              onClick={() => setActiveTab("questions")}
              className={`py-3 px-1 border-b-2 text-sm font-medium transition flex items-center gap-2 ${
                activeTab === "questions"
                  ? "border-caramel text-caramel"
                  : "border-transparent text-cocoa hover:text-espresso"
              }`}
            >
              <HelpCircle size={14} />
              Questions ({questions.length})
            </button>
            <button
              onClick={() => setActiveTab("notes")}
              className={`py-3 px-1 border-b-2 text-sm font-medium transition flex items-center gap-2 ${
                activeTab === "notes"
                  ? "border-caramel text-caramel"
                  : "border-transparent text-cocoa hover:text-espresso"
              }`}
            >
              <StickyNote size={14} />
              Notes & Status
            </button>
          </div>
        </div>

        {/* MODAL BODY - Scrollable with tab content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* TAB 1: CONTACT INFORMATION */}
          {activeTab === "contact" && (
            <div className="space-y-4 max-w-2xl">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-espresso mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., Sarah Johnson"
                    className="w-full px-4 py-3 rounded-lg border border-cocoa/40 text-espresso focus:border-caramel focus:ring-2 focus:ring-caramel/20 outline-none transition placeholder:text-cocoa/40"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-espresso mb-2">
                    Company *
                  </label>
                  <input
                    type="text"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    placeholder="e.g., Google"
                    className="w-full px-4 py-3 rounded-lg border border-cocoa/40 text-espresso focus:border-caramel focus:ring-2 focus:ring-caramel/20 outline-none transition placeholder:text-cocoa/40"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-espresso mb-2">
                  Role *
                </label>
                <input
                  type="text"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  placeholder="e.g., Senior Software Engineer"
                  className="w-full px-4 py-3 rounded-lg border border-cocoa/40 focus:border-caramel text-espresso focus:ring-2 focus:ring-caramel/20 outline-none transition placeholder:text-cocoa/40"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-espresso mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="sarah@company.com"
                  className="w-full px-4 py-3 rounded-lg border border-cocoa/40 focus:border-caramel text-espresso focus:ring-2 focus:ring-caramel/20 outline-none transition placeholder:text-cocoa/40"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-espresso mb-2">
                  LinkedIn URL
                </label>
                <input
                  type="url"
                  value={linkedin}
                  onChange={(e) => setLinkedin(e.target.value)}
                  placeholder="linkedin.com/in/sarahjohnson"
                  className="w-full px-4 py-3 rounded-lg border border-cocoa/40 focus:border-caramel text-espresso focus:ring-2 focus:ring-caramel/20 outline-none transition placeholder:text-cocoa/40"
                />
              </div>
            </div>
          )}

          {/* TAB 2: MESSAGE DRAFT */}
          {activeTab === "message" && (
            <div className="space-y-4 max-w-3xl">
              <div>
                <label className="block text-sm font-medium text-espresso mb-2">
                  Draft your message
                </label>
                <p className="text-xs text-cocoa mb-3">
                  Write a personalized message to request a coffee chat
                </p>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Hi Sarah,&#10;&#10;I came across your profile and was impressed by your work at Google. I'm currently exploring opportunities in software engineering and would love to learn more about your experience.&#10;&#10;Would you be open to a quick 20-minute coffee chat? I'd love to hear about your career journey.&#10;&#10;Best regards"
                  rows={12}
                  className="w-full px-4 py-3 rounded-lg border border-cocoa/40 focus:border-caramel text-espresso focus:ring-2 focus:ring-caramel/20 outline-none transition resize-none font-sans text-sm leading-relaxed placeholder:text-cocoa/40"
                />
                <p className="text-xs text-cocoa mt-2">
                  {message.length} characters
                </p>
              </div>
            </div>
          )}

          {/* TAB 3: QUESTIONS */}
          {activeTab === "questions" && (
            <div className="space-y-4 max-w-3xl">
              <div>
                <label className="block text-sm font-medium text-espresso mb-2">
                  Questions to Ask
                </label>
                <p className="text-xs text-cocoa mb-3">
                  Prepare thoughtful questions to make the most of your conversation
                </p>

                {/* ADD QUESTION INPUT */}
                <div className="flex gap-2 mb-4">
                  <input
                    type="text"
                    value={newQuestion}
                    onChange={(e) => setNewQuestion(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddQuestion();
                      }
                    }}
                    placeholder="e.g., What skills are most valuable in your role?"
                    className="flex-1 px-4 py-2.5 rounded-lg border border-cocoa/40 focus:border-caramel text-espresso focus:ring-2 focus:ring-caramel/20 outline-none transition text-sm placeholder:text-cocoa/40" 
                  />
                  <button
                    onClick={handleAddQuestion}
                    className="px-4 py-2.5 rounded-lg bg-caramel text-white hover:opacity-90 transition flex items-center gap-2"
                  >
                    <Plus size={16} />
                    Add
                  </button>
                </div>

                {/* QUESTIONS LIST */}
                {questions.length === 0 ? (
                  <div className="text-center py-8 bg-cream rounded-lg border-2 border-dashed border-sand">
                    <p className="text-sm text-cocoa">No questions added yet</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {questions.map((question, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-3 p-3 rounded-lg  border border-cocoa/40 bg-white hover:border-caramel transition group"
                      >
                        <span className="text-sm font-semibold text-cocoa w-6 shrink-0">
                          {index + 1}.
                        </span>
                        <span className="flex-1 text-sm text-espresso">
                          {question}
                        </span>
                        <button
                          onClick={() => handleRemoveQuestion(index)}
                          className="opacity-0 group-hover:opacity-100 p-1.5 rounded-md text-cocoa/70 hover:text-rose-600 hover:bg-rose-50 transition shrink-0"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB 4: NOTES & STATUS */}
          {activeTab === "notes" && (
            <div className="space-y-4 max-w-3xl">
              <div>
                <label className="block text-sm font-medium text-espresso mb-2">
                  Status *
                </label>
                <Select value={status} onValueChange={(v) => setStatus(v as ChatStatus)}>
                  <SelectTrigger className="h-auto px-4 py-3 border-cocoa/40 text-cocoa bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending - Not sent yet</SelectItem>
                    <SelectItem value="sent">Sent - Waiting for response</SelectItem>
                    <SelectItem value="done">Done - Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-espresso mb-2">
                  Scheduled Date
                </label>
                <DatePicker
                  value={scheduledDate}
                  onChange={setScheduledDate}
                  className="w-full px-4 py-3 rounded-lg bg-white border border-cocoa/40 focus:border-caramel text-cocoa focus:ring-2 focus:ring-caramel/20 outline-none transition"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-espresso mb-2">
                  Notes
                </label>
                <p className="text-xs text-cocoa mb-3">
                  Add notes before or after your conversation
                </p>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Key takeaways, action items, or things to remember..."
                  rows={8}
                  className="w-full px-4 py-3 rounded-lg border border-cocoa/40 focus:border-caramel text-espresso focus:ring-2 focus:ring-caramel/20 outline-none transition resize-none font-sans text-sm leading-relaxed placeholder:text-cocoa/40"
                />
              </div>
            </div>
          )}
        </div>

        {/* MODAL FOOTER */}
        <div className="border-t border-cocoa/20 px-6 py-4 flex items-center justify-end gap-3 pt-4">
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-6 py-2.5 rounded-lg border border-cocoa/50 text-espresso hover:bg-cocoa/5 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving || !name.trim() || !company.trim() || !role.trim()}
              className="px-6 py-2.5 rounded-lg bg-caramel text-white hover:opacity-90 transition disabled:opacity-50"
            >
              {saving ? 'Saving...' : (editChat ? 'Save Changes' : 'Add Coffee Chat')}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}